import React, { useEffect, useMemo, useState } from "react";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { GripVertical, Settings, Trash2, ChevronDown } from "lucide-react";
import { getFieldIcon, getColumnSpanClass, getRowSpanClass } from "../utils/field-utils";
import { Field } from "@/services/entity-view.service";
import { DataTypeEnum } from "@/services/entity.service";
import { FilterTrigger } from "@/components/filter/filter-trigger";
import { useEntitySchemaByName } from "@/hooks/schema/use-entity-schema-by-name";
import { FormField } from "@/hooks/schema/use-schema";
import { EntityViewComponentSelection } from "./entity-view-component-selection";
import { useEntityViewComponents } from "@/hooks/use-entity-view-components";
import { EntityViewComponentMapping } from "@/services/entity-view-components.service";

interface SortableFieldProps {
  field: Field;
  sectionId: string;
  onRemove: (sectionId: string, fieldId: string) => void;
  onUpdateWidth: (sectionId: string, fieldId: string, width: 1 | 2 | 3) => void;
  onUpdateHeight: (sectionId: string, fieldId: string, height: 1 | 2 | 3) => void;
  onUpdateRelationFieldDisplay?: (
    sectionId: string,
    fieldId: string,
    relationFieldToDisplay: string,
  ) => void;
  onSetTitle?: (fieldId: string, isTitle: boolean) => void;
  isDraggingAny: boolean;
  entityName?: string;
  relationData?: {
    target_entity: {
      data: {
        id: string;
        name: string;
      };
    };
  };
}

export function SortableField({
  field,
  sectionId,
  onRemove,
  onUpdateWidth,
  onUpdateHeight,
  onUpdateRelationFieldDisplay,
  onSetTitle,
  relationData,
}: SortableFieldProps) {
  const [isPopoverOpen, setIsPopoverOpen] = useState(false);

  // Get the target entity schema for relation fields
  const targetEntityName = relationData?.target_entity?.data?.name;
  const { entitySchemaWithRelations: targetEntitySchema } = useEntitySchemaByName(
    targetEntityName || "",
  );

  const isRelationField = field.type === "relation";

  // Only show field selector for relation fields that have relation data
  const shouldShowFieldSelector = isRelationField && relationData && onUpdateRelationFieldDisplay;

  const {
    data: mappingData,
    getComponentMapping,
    createViewComponentMappingMutation,
    updateViewComponentMappingMutation,
    deleteViewComponentMappingMutation,
  } = useEntityViewComponents();

  const [componentMapping, setComponentMapping] = useState(
    shouldShowFieldSelector ? getComponentMapping({ entityName: targetEntityName! }) : undefined,
  );

  useEffect(() => {
    if (shouldShowFieldSelector && mappingData) {
      setComponentMapping(getComponentMapping({ entityName: targetEntityName! }));
    }
  }, [shouldShowFieldSelector, mappingData]);

  const handleSetComponentMapping = (mapping?: EntityViewComponentMapping) => {
    const orig = getComponentMapping({ entityName: targetEntityName! });

    //ignored
    if (!orig && !mapping) return;

    if (orig && !mapping) {
      //delete mapping
      deleteViewComponentMappingMutation(orig.id!);
      return;
    }

    if (!orig) {
      //create
      createViewComponentMappingMutation({
        input: {
          entity_set: {
            id: targetEntitySchema?.id!,
          },
          component: mapping!.component,
          config: mapping!.config as string,
        },
      });
      return;
    }

    //update
    updateViewComponentMappingMutation({
      input: {
        id: orig.id,
        component: mapping!.component,
        config: mapping!.config as string,
      },
    });
  };

  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: field.id,
    data: { field, sectionId, fromPalette: false },
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  const handleWidthChange = (width: 1 | 2 | 3) => {
    onUpdateWidth(sectionId, field.id, width);
    setIsPopoverOpen(false);
  };

  const handleHeightChange = (height: 1 | 2 | 3) => {
    onUpdateHeight(sectionId, field.id, height);
    setIsPopoverOpen(false);
  };

  const handleRelationFieldSelect = (selectedField: FormField) => {
    if (onUpdateRelationFieldDisplay) {
      onUpdateRelationFieldDisplay(sectionId, field.id, selectedField.name);
    }
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`relative transition-all duration-200 ${getColumnSpanClass(field.width)} ${getRowSpanClass(field.height)} ${
        isDragging ? "z-50 opacity-30" : ""
      }`}
      {...attributes}
    >
      {isDragging ? (
        // Simple colored placeholder when dragging
        <div className="h-full w-full rounded border-2 border-dashed border-blue-400 bg-blue-200" />
      ) : (
        <div className="group relative flex h-full flex-col gap-2 rounded-lg border bg-white p-3 hover:shadow-sm">
          {/* Field Header */}
          <div className="flex min-h-[24px] items-center gap-2">
            <div {...listeners} className="cursor-grab active:cursor-grabbing">
              <GripVertical className="h-4 w-4 text-gray-400" />
            </div>
            {getFieldIcon(field.icon as DataTypeEnum)}
            <span className="flex-1 truncate text-sm font-medium">{field.name}</span>
            {field.is_title && (
              <span className="rounded-full border px-1.5 text-[11px] text-muted-foreground">
                Title
              </span>
            )}

            {/* Inline Field Selector for Relation Fields */}

            {shouldShowFieldSelector && (
              <EntityViewComponentSelection
                onSelect={handleSetComponentMapping}
                entityName={targetEntityName ?? ""}
                mapping={componentMapping}
                relation_field_to_display={field.relation_field_to_display}
                handleRelationFieldSelect={handleRelationFieldSelect}
                targetEntitySchema={targetEntitySchema!}
              />
            )}

            {/* Field Controls */}
            <div className="flex items-center gap-1 opacity-0 transition-opacity group-hover:opacity-100">
              <Popover open={isPopoverOpen} onOpenChange={setIsPopoverOpen}>
                <PopoverTrigger asChild>
                  <Button size="sm" variant="ghost" className="h-6 w-6 p-0">
                    <Settings className="h-3 w-3" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-64" align="end">
                  <div className="space-y-4">
                    <div>
                      <Label className="text-xs font-medium">Width (Columns)</Label>
                      <div className="mt-1 flex gap-1">
                        {[1, 2, 3].map((width) => (
                          <Button
                            key={width}
                            size="sm"
                            variant={field.width === width ? "default" : "outline"}
                            className="h-8 w-8 p-0"
                            onClick={() => handleWidthChange(width as 1 | 2 | 3)}
                          >
                            {width}
                          </Button>
                        ))}
                      </div>
                    </div>
                    <div>
                      <Label className="text-xs font-medium">Height (Rows)</Label>
                      <div className="mt-1 flex gap-1">
                        {[1, 2, 3].map((height) => (
                          <Button
                            key={height}
                            size="sm"
                            variant={field.height === height ? "default" : "outline"}
                            className="h-8 w-8 p-0"
                            onClick={() => handleHeightChange(height as 1 | 2 | 3)}
                          >
                            {height}
                          </Button>
                        ))}
                      </div>
                    </div>
                    {!isRelationField && onSetTitle && (
                      <label className="flex cursor-pointer items-center gap-2 text-xs font-medium">
                        <Checkbox
                          checked={!!field.is_title}
                          onCheckedChange={(checked) => onSetTitle(field.id, checked === true)}
                        />
                        Use as record title
                      </label>
                    )}
                  </div>
                </PopoverContent>
              </Popover>

              <Button
                size="sm"
                variant="ghost"
                onClick={() => onRemove(sectionId, field.id)}
                className="h-6 w-6 p-0 text-red-600 hover:text-red-700"
              >
                <Trash2 className="h-3 w-3" />
              </Button>
            </div>
          </div>

          {/* Field Content Area */}
          <div className="flex flex-1 items-center justify-center rounded border border-dashed border-gray-200 text-xs text-gray-500">
            <div className="text-center">
              <div className="text-xs opacity-75">
                {field.width}×{field.height}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
